import { useI18n } from '@ggs/i18n'
import { delay } from 'lodash'
import { Button } from '../../index'
import { useDrupalApi, useResolveResourceHook } from '../../../local'
import { addNotification, useDispatch } from '@ggs/store'
import { useConfirmDialog } from '@ggs/ui/hooks'
import './RemoveProjectResourceAction.scss'

/**
 * @typedef {Object} RemoveProjectResourceActionProps
 * @property {string} resourceType
 * @property {string} resourceId
 * @property {string} projectId
 */

/**
 * Remove project resource action button.
 * @param {RemoveProjectResourceActionProps} props
 * @return {JSX.Element}
 */
export const RemoveProjectResourceAction = ({ resourceType, resourceId, projectId }) => {
  const { t } = useI18n()
  const { getProject, removeResourceFromProject } = useDrupalApi()
  const { resolveResourceEntity, notifyResourceNotFound } = useResolveResourceHook({
    resourceType,
    resourceId,
  })
  const dispatch = useDispatch()
  const isStorybook = `${process.env.STORYBOOK}` === 'yes'

  /**
   * Process resource deletion from specific project.
   */
  const processRemoveProjectResource = async () => {
    const resourceEntity = await resolveResourceEntity()
    if (!resourceEntity) {
      notifyResourceNotFound()
      return
    }

    const projectNode = await getProject(projectId)
    if (!projectNode) {
      dispatch(
        addNotification(
          'project_not_found',
          'error',
          t('Project "{{projectId}}" entity cannot be loaded.', {
            resourceId: resourceId,
          })
        )
      )

      return
    }

    const updatedProjectNode = await removeResourceFromProject(projectNode, resourceEntity)
    if (updatedProjectNode) {
      dispatch(
        addNotification(
          'remove_resource_success',
          'success',
          t('Resource was successfully removed from project.')
        )
      )

      // Allow notifications are displayed and post delete actions happen with delayed refresh page.
      if (!isStorybook) {
        delay(() => {
          window.location.reload()
        }, 2000)
      }
    } else {
      dispatch(addNotification('remove_resource_failed', 'error', t('Resource removal failed.')))
    }
  }

  const { handleOpenClick: confirmResourceProjectDeletion, ConfirmDialogComponent } =
    useConfirmDialog({
      acceptCallback: async () => {
        await processRemoveProjectResource()
      },
      cancelCallback: () => {},
      acceptLabel: t('Delete Resource'),
      cancelLabel: t('Cancel'),
      actionMessage: t('Please confirm that you want to delete this resource from your project.'),
      dialogTitle: t('Delete Resource From This Project?'),
      submitBtnClasses: 'delete-resource-btn--submit',
      cancelBtnClasses: 'delete-resource-btn--cancel'
    })

  return (
    <>
      <Button
        label={t('Delete Resource')}
        onClick={confirmResourceProjectDeletion}
        classes={{
          root: 'btn--delete'
        }}
      />
      {ConfirmDialogComponent}
    </>
  )
}
